/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * OrganizationRegistrationPage
 *
 * React page for full organization onboarding, including:
 * - Multi-section organization + admin user registration form
 * - Cleansing and normalization of organization payload before submission
 * - API call to `/arcee/user/register-admin/` to create organization and its admin
 * - Inline server-side validation error mapping for form fields
 * - Success dialog showing verification email message
 * - Redirect to sign-in after successful registration
 * 
 * All UI dialogs (success/error) use the shared AlertDialog component.
 */
import React, { useState } from "react";
import OrganizationRegistrationForm from "../components/OrganizationRegistrationForm";
import APIHelper from "../api/APIHelper";
import envConfig from "../config/envConfig";
import { useNavigate } from "react-router-dom";
import AlertDialog from "../components/common/AlertDialog"; // update path as needed

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

function cleanOrgPayload(org) {
  return {
    ...org,
    registered_address: org.registered_address.trim() ? org.registered_address : null,
    government_id1: org.government_id1.trim() ? org.government_id1 : null,
    government_id2: org.government_id2.trim() ? org.government_id2 : null,
  };
}

function mapServerErrors(errBody) {
  if (!errBody || typeof errBody !== 'object') return {};

  const mapped = {};

  const orgFieldNames = [
    'name',
    'description',
    'registered_address',
    'city',
    'state',
    'country',
    'pincode',
    'tax_id',
    'government_id1',
    'government_id2'
  ];

  const adminFieldNames = [
    'username',
    'email',
    'password',
    'first_name',
    'last_name'
  ];

  for (const [key, value] of Object.entries(errBody)) {
    const msg = Array.isArray(value) ? value.join(' ') : String(value);

    if (key === 'domain_name') {
      // maps to Domain Name field
      mapped.domain_name = msg;
    } else if (orgFieldNames.includes(key)) {
      // org fields → org_name, org_city, etc.
      mapped[`org_${key}`] = msg;
    } else if (adminFieldNames.includes(key)) {
      // admin fields → admin_username, admin_email, etc.
      mapped[`admin_${key}`] = msg;
    } else if (key === 'error') {
      mapped.domain_name = msg;
    }
  }

  return mapped;
}

function OrganizationRegistrationPage() {
  const navigate = useNavigate();
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialog, setSuccessDialog] = useState('');

  const handleRegister = async (org, admin, domain_name, setErrors) => {
    try {
      const payload = {
        domain_name,
        organization: cleanOrgPayload(org),
        admin
      };
      const response = await apiHelper.apiCall("/arcee/user/register-admin/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let errBody;
        try {
          errBody = await response.json();
        } catch {
          errBody = null;
        }

        const mapped = mapServerErrors(errBody);

        if (Object.keys(mapped).length === 0) {
          // fallback generic error
          setErrors({ api_error: 'Something went wrong. Please try again.' });
        } else {
          setErrors(mapped);
        }
        return;
      }

      // Show success dialog
      setSuccessDialog(`Organization and admin user have been registered, verification email has been sent to ${admin.email}. Verify your email to sign in`);
      setShowSuccessDialog(true);
    } catch (error) {
      console.error(error);
      setErrors({
        api_error: 'Unable to connect to server. Please try again later.',
      });
    }
  };

  return (
    <>
      <OrganizationRegistrationForm onSubmit={handleRegister} />
      <AlertDialog
        open={showSuccessDialog}
        type="success"
        title="Registration Successful!"
        message={successDialog}
        okLabel="Sign In"
        showCancel={false}
        onOk={() => {
          setShowSuccessDialog(false);
          navigate("/sign-in");
        }}
      />
    </>
  );
}

export default OrganizationRegistrationPage;
